<template>
  <div
    class="relative overflow-auto overscroll-none tablet:max-h-[calc(100vh-300px)] py-4 tablet:px-4 pb-[100px] tablet:pb-4"
  >
    <form
      id="search-modal"
      class="relative flex items-center px-2.5 py-1 rounded border border-border"
      @submit.prevent="onFormSubmit"
    >
      <div v-if="isFocused" class="icon" @click="clear">
        <SpriteSymbol name="menu-close" class="size-4 text-primary" />
      </div>
      <span v-else class="icon">
        <SpriteSymbol name="search-2" class="size-4" />
      </span>

      <input
        v-model="searchTerm"
        type="text"
        class="input border-none shadow-none grow w-auto"
        :placeholder="$texts('enterSearchTerm', 'Enter a search term')"
      />
      <button
        class="button is-primary rounded-full h-[2.25em] !px-[0.75em] ml-2"
        :class="{
          'is-loading': isLoading,
        }"
      >
        {{ $texts('search', 'Search') }}
      </button>
    </form>

    <div
      :class="{
        'opacity-50': isLoading,
      }"
    >
      <div v-if="results.length" id="search-results">
        <KeyboardNavigationProvider tag="ol" :is-enabled="visible">
          <Item
            v-for="(item, index) in results"
            :key="item.id"
            v-bind="item"
            @click="onItemClick(item, index)"
          />
        </KeyboardNavigationProvider>
      </div>

      <div v-else-if="hasNoResults || hasError" class="text-grey-dark pt-4">
        <p v-if="hasError">
          {{
            $texts(
              'searchNotAvailable',
              'The search is currently not available.',
            )
          }}
        </p>
        <template v-else>
          <h3 class="title is-5">
            {{ $texts('noResultsTitle', 'No content found') }}
          </h3>
          <p>
            {{
              $texts(
                'noResultsText',
                'Sorry, we did not find any matching entries with this search term. Try a different spelling or another search term.',
              )
            }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import type { SearchHitContent } from '~/layers/search/server/api/search/fulltext'
import Item from './Item.vue'
import { getFilteredSearchText } from '~/layers/search/helpers/search'
import { DomainId } from '#graphql-operations'

defineProps<{
  visible: boolean
}>()

const router = useRouter()
const { $texts } = useEasyTexts()
const { activeDomain } = useSiteContext()
const language = useCurrentLanguage()
const { trackEvent, getAnalyticsRouteName } = useAnalytics()

const searchTerm = ref('') // The current search term in the input.
const resultSearchTerm = ref('') // The search term for which the results are displayed.
const isLoadingResults = ref(false)
const hasError = ref(false)
const results = ref<SearchHitContent[]>([])
const isFocused = ref(false)

const searchTermFiltered = computed(() =>
  getFilteredSearchText(searchTerm.value),
)

const hasNoResults = computed(
  () =>
    !!resultSearchTerm.value &&
    !results.value.length &&
    !!searchTermFiltered.value,
)

const isLoading = computed(
  () =>
    isLoadingResults.value ||
    searchTermFiltered.value !== resultSearchTerm.value,
)

async function fetchResults(
  text: string,
): Promise<{ error?: boolean; hits: SearchHitContent[] }> {
  // No need to request when no search term is given.
  if (!text) {
    return {
      hits: [],
      error: false,
    }
  }

  return $fetch('/api/search/fulltext', {
    query: {
      langcode: language.value,
      domain: activeDomain,
      text,
    },
  }).catch(() => {
    return {
      hits: [],
      error: true,
    }
  })
}

const loadResults = useDebounceFn(async () => {
  isLoadingResults.value = true

  const text = searchTermFiltered.value
  const response = await fetchResults(text)

  results.value = response.hits
  resultSearchTerm.value = text
  isLoadingResults.value = false
  hasError.value = !!response.error

  if (searchTerm.value) {
    isFocused.value = true
  } else {
    isFocused.value = false
  }

  trackEvent({
    event: 'viewSearchResults',
    searchTerm: text,
    searchResultsCount: results.value.length,
  })
}, 500)

function onItemClick(item: SearchHitContent, index: number) {
  trackEvent({
    event: 'clickSearchResult',
    searchTerm: searchTermFiltered.value,
    searchResultsCount: results.value.length,
    searchResultClickedPageLocation: document.location.origin + item.url,
    searchResultClickedPageTitle: item.title,
    searchResultClickedRouteName: getAnalyticsRouteName(),
    searchResultClickedIndex: index,
    searchResultClickedNodeID: item.nid,
  })
  router.push(item.url)
}

function onFormSubmit() {
  if (activeDomain !== DomainId.zuerich_com || !searchTermFiltered.value) {
    return
  }

  router.push({
    name: 'search-what-to-do-zurich',
    query: {
      text: searchTermFiltered.value,
    },
  })
}

function clear() {
  isFocused.value = false
  searchTerm.value = ''
}

// Load results when the filtered search term changes.
watch(searchTermFiltered, loadResults)
</script>
